import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { RefreshCw, Clock, StopCircle, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranscription } from '../../hooks/useTranscription';
interface ProcessingCardProps {
  startTime: number;
  videoId?: string;
  url?: string;
  taskId: string;
}
export function ProcessingCard({
  startTime,
  videoId,
  url,
  taskId
}: ProcessingCardProps) {
  const navigate = useNavigate();
  const {
    stopTranscription
  } = useTranscription();
  const [elapsedTime, setElapsedTime] = useState<string>('00:00');
  const [isHovering, setIsHovering] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  useEffect(() => {
    const intervalId = setInterval(() => {
      const now = Date.now();
      const elapsed = Math.floor((now - startTime) / 1000); // elapsed time in seconds
      const minutes = Math.floor(elapsed / 60).toString().padStart(2, '0');
      const seconds = (elapsed % 60).toString().padStart(2, '0');
      setElapsedTime(`${minutes}:${seconds}`);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [startTime]);
  const handleStopTranscription = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isStopping) return;
    setIsStopping(true);
    try {
      await stopTranscription(taskId);
    } finally {
      setIsStopping(false);
    }
  };
  const handleCardClick = () => {
    navigate(`/app/transcriptions/${taskId}`);
  };
  return <Card className="h-full transition-all duration-200 cursor-pointer hover:border-primary/40 hover:shadow-md hover:shadow-primary/5 relative overflow-hidden" onClick={handleCardClick} onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)} data-id="element-1570">
      <div className="w-full h-32 sm:h-40 bg-gray-800 rounded-t-lg flex items-center justify-center" data-id="element-1571">
        <div className="flex flex-col items-center justify-center" data-id="element-1572">
          <RefreshCw size={32} className="text-primary animate-spin mb-2" data-id="element-1573" />
          <p className="text-sm text-gray-300" data-id="element-1574">Processing Video</p>
          <p className="text-xs text-gray-400 mt-1" data-id="element-1575">
            Extracting audio and metadata...
          </p>
        </div>
      </div>
      {/* Stop button overlay that appears on hover */}
      {isHovering && <div className="absolute inset-0 bg-black/70 flex items-center justify-center transition-opacity duration-200" data-id="element-1576">
          <Button variant="destructive" size="sm" className="flex items-center gap-1.5" onClick={handleStopTranscription} disabled={isStopping} data-id="element-1577">
            {isStopping ? <>
                <RefreshCw size={16} className="animate-spin" data-id="element-1578" />
                Stopping...
              </> : <>
                <StopCircle size={16} data-id="element-1579" />
                Stop Transcription
              </>}
          </Button>
        </div>}
      <CardContent className="p-3 md:p-4" data-id="element-1580">
        <div className="flex justify-between items-center mb-2" data-id="element-1581">
          <div className="flex items-center text-xs text-muted-foreground" data-id="element-1582">
            <Clock size={12} className="mr-1" data-id="element-1583" />
            <span data-id="element-1584">{elapsedTime}</span>
          </div>
          <div data-id="element-1585">
            <span className="flex items-center gap-1 text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full" data-id="element-1586">
              <RefreshCw size={12} className="animate-spin" data-id="element-1587" />
              <span data-id="element-1588">Processing</span>
            </span>
          </div>
        </div>
        <h3 className="font-medium text-sm md:text-base line-clamp-2 mb-1" data-id="element-1589">
          {url ? `Processing: ${url}` : `Processing video...`}
        </h3>
        <p className="text-xs text-muted-foreground truncate" data-id="element-1590">
          ID: {videoId || taskId}
        </p>
        <div className="mt-2" data-id="element-1591">
          <div className="w-full h-1.5 bg-secondary rounded-full overflow-hidden" data-id="element-1592">
            <div className="h-full bg-primary rounded-full animate-pulse" data-id="element-1593"></div>
          </div>
        </div>
      </CardContent>
    </Card>;
}